import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import HomePage from './Pages/HomePage/HomePage';
import AOS from 'aos';
import 'aos/dist/aos.css';
import GalleryPage from './Pages/GalleryPage/GalleryPage.jsx';
import HomeTest from './Components/HomeTest/HomeTest.jsx';
import Loading from './Components/loading/Loading.jsx';
import { HelmetProvider } from 'react-helmet-async';
import { Helmet } from 'react-helmet';

const App = () => {
  const [isHomePageVisible, setIsHomePageVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isColorPickerVisible, setIsColorPickerVisible] = useState(false);
  const [colorDescription, setColorDescription] = useState('');

  const colorDescriptions = {
    '#997e5b': {
      name: 'Sandstone Whisper (default)',
      description: 'A warm, earthy hue reminiscent of natural sandstone.',
    },
    '#8b5d5d': {
      name: 'Rosewood Ember',
      description: 'Suggests a rich, rose color with a hint of ember warmth.',
    },
    '#778147': {
      name: 'Willow Wisp',
      description: 'A soft, natural green evoking the gentleness of willow leaves.',
    },
    '#9f9fa8': {
      name: 'Moonlit Frost',
      description: 'A subtle, serene quality with a hint of moonlight.',
    },
    '#947C8E': {
      name: 'Ethereal Mist',
      description: 'Conveys a dreamy, delicate lavender with a touch of mystery.',
    },
    '#F2BF5E': {
      name: 'Sunlit Aura',
      description: 'Radiates warmth and brightness, like a golden sunrise.',
    }
  };

  useEffect(() => {
    AOS.init({
      offset: 100,
      duration: 800,
      easing: 'ease',
      once: false,
    });

    const handleResize = () => {
      AOS.refresh();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const isHomePageShown = sessionStorage.getItem('isHomePageShown');
    if (isHomePageShown) {
      setIsHomePageVisible(true);
    }

    // Retrieve and apply the selected color from sessionStorage
    const storedColor = sessionStorage.getItem('selectedColor');
    if (storedColor) {
      document.documentElement.style.setProperty('--color-primary', storedColor);
      const description = colorDescriptions[storedColor];
      if (description) {
        setColorDescription(`${description.name}: ${description.description}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleButtonClick = (buttonNumber) => {
    if (buttonNumber === 1) {
      setIsColorPickerVisible(true);
    }
  };

  const handleColorClick = (color) => {
    document.documentElement.style.setProperty('--color-primary', color);
    sessionStorage.setItem('selectedColor', color);
    const description = colorDescriptions[color];
    if (description) {
      setColorDescription(`${description.name}: ${description.description}`);
    }
  };

  const startJourney = () => {
    sessionStorage.setItem('isHomePageShown', 'true');
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setIsHomePageVisible(true);
    }, 5000);
  };

  // Detect if the user-agent is a robot
  const isRobot = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /googlebot|bingbot|slackbot/.test(userAgent);
  };

  // If it's a robot, bypass the conditions
  if (isRobot()) {
    return (
      <>
        <HelmetProvider>
          <Helmet>
            <title>Mohamed Metwally's Portfolio</title>
            <meta name="keywords" content="
  mohamed metwally,
  mo-metwally,
  m.metwally,
  mohamed metwally portfolio,
  mohamed metwally web developer,
  mohamed metwally front-end,
  mohamed metwally developer,
  mohamed metwally portfolio website,
  portfolio,
  website,
  محمد متولي مطور ويب,
  محمد متولي تطوير الواجهات الأمامية,
  محمد متولي مطور,
  موقع محمد متولي,
  موقع شخصي,
  موقع تعريفي,
  موقع إلكتروني
  front-end developer,
  React.js,
  GSAP,
  Tailwind CSS,
  dynamic web applications,
  responsive web design,
  self-management,
  collaboration,
  high-quality web solutions,
  innovation,
  excellence,
  interactive websites,
  complex web applications,
  extensive experience,
  seamless user experiences,
  creative ideas,
  practical solutions,
  مطور واجهات أمامية,
  React.js,
  GSAP,
  Tailwind CSS,
  تطبيقات الويب الديناميكية,
  تصميم الويب المتجاوب,
  إدارة الذات,
  التعاون,
  حلول الويب عالية الجودة,
  الابتكار,
  التفوق,
  المواقع التفاعلية,
  تطبيقات الويب المعقدة,
  الخبرة الواسعة,
  تجارب المستخدم السلسة,
  الأفكار الإبداعية,
  الحلول العملية,
  Programming,
  Company,
  Creative,
  Technical,
  Services,
  Diverse,
  Expert,
  Consultation,
  Design,
  Installation,
  Maintenance,
  Technological,
  Security,
  Law,
  Legal,
  Consulting,
  Legal services,
  Local,
  International,
  Dealings,
  Vision,
  Value,
  UI,
  Design,
  Hotel,
  Website,
  Addresses,
  Services,
  Reservation,
  Capabilities,
  Creative agency,
  Graphic,
  Design,
  Video,
  Editing,
  User-friendly,
  Platform,
  Intuitive,
  Navigation,
  Visually appealing,
  Layouts,
  Enjoyable,
  Learning,
  E-commerce,
  Website,
  Modern,
  Kitchenware,
  Unique,
  Assortment,
  Graduation,
  Project,
  Programming company,
  Creative technical services,
  Diverse technical services,
  Expert consultation,
  Design and installation,
  Maintenance services,
  Technological needs,
  Security needs,
  Law and legal consulting,
  Legal services,
  Local dealings,
  International dealings,
  Vision and value,
  UI design,
  Hotel website,
  Addresses,
  Reservation capabilities,
  Creative agency,
  Graphic design,
  Video editing services,
  User-friendly platform,
  Intuitive navigation,
  Visually appealing layouts,
  Enjoyable learning,
  E-commerce website,
  Modern kitchenware,
  Unique assortment,
  Graduation project,
  برمجة,
  شركة,
  إبداعي,
  تقني,
  خدمات,
  متنوعة,
  خبير,
  استشارة,
  تصميم,
  تركيب,
  صيانة,
  تكنولوجي,
  أمان,
  قانون,
  قانوني,
  استشارات,
  خدمات قانونية,
  محلي,
  دولي,
  تعاملات,
  رؤية,
  قيمة,
  واجهة المستخدم,
  تصميم,
  فندق,
  موقع إلكتروني,
  عناوين,
  خدمات,
  حجز,
  قدرات,
  وكالة إبداعية,
  تصميم جرافيك,
  فيديو,
  تحرير,
  سهل الاستخدام,
  منصة,
  بديهي,
  تنقل,
  جذاب بصريًا,
  تخطيطات,
  ممتع,
  تعلم,
  تجارة إلكترونية,
  موقع إلكتروني,
  حديث,
  أدوات المطبخ,
  فريد,
  مجموعة,
  تخرج,
  مشروع,
  شركة برمجة,
  خدمات تقنية إبداعية,
  خدمات تقنية متنوعة,
  استشارة خبير,
  تصميم وتركيب,
  خدمات صيانة,
  احتياجات تكنولوجية,
  احتياجات أمان,
  قانون واستشارات قانونية,
  خدمات قانونية,
  تعاملات محلية,
  تعاملات دولية,
  رؤية وقيمة,
  تصميم واجهة المستخدم,
  موقع فندق,
  عناوين,
  قدرات حجز,
  وكالة إبداعية,
  تصميم جرافيك,
  خدمات تحرير الفيديو,
  منصة سهلة الاستخدام,
  تنقل بديهي,
  تخطيطات جذابة بصريًا,
  تعلم ممتع,
  موقع تجارة إلكترونية,
  أدوات مطبخ حديثة,
  مجموعة فريدة,
  مشروع تخرج
" />
          </Helmet>
        </HelmetProvider>

        <Router>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/Gallery" element={<GalleryPage />} />
          </Routes>
        </Router>
      </>
    );
  }

  if (!isHomePageVisible && !isLoading) {
    if (isColorPickerVisible) {
      return (
        <div className='enter-container'>
          <div className='enter-abs'>
            <HomeTest />
          </div>
          <div className='chose'>
            <h2>Pick A Color</h2>
            <h4>
              <span>{colorDescription.split(':')[0]}</span>:{colorDescription.split(':')[1]}
            </h4>
            <br />
            <div className='colors'>
              <div className='color' style={{ backgroundColor: '#997e5b' }} onClick={() => handleColorClick('#997e5b')}>
                Sandstone Whisper
              </div>
              <div className='color' style={{ backgroundColor: '#8b5d5d' }} onClick={() => handleColorClick('#8b5d5d')}>
                Rosewood Ember
              </div>
              <div className='color' style={{ backgroundColor: '#778147' }} onClick={() => handleColorClick('#778147')}>
                Willow Wisp
              </div>
              <div className='color' style={{ backgroundColor: '#9f9fa8' }} onClick={() => handleColorClick('#9f9fa8')}>
                Moonlit Frost
              </div>
              <div className='color' style={{ backgroundColor: '#947C8E' }} onClick={() => handleColorClick('#947C8E')}>
                Ethereal Mist
              </div>
              <div className='color' style={{ backgroundColor: '#F2BF5E' }} onClick={() => handleColorClick('#F2BF5E')}>
                Sunlit Aura
              </div>
              <div className='selectedColor'>{""}</div>
            </div>
            <div className='chose-btn'>
              <div className='Scolor'>
                <span>
                  <button className='btn Scolors' onClick={startJourney}>
                    Start Your Journey
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        <HelmetProvider>
          <Helmet>

            <title>Mohamed Metwally's Portfolio</title>

            <meta name="keywords" content="
  mohamed metwally,
  mo-metwally,
  m.metwally,
  mohamed metwally portfolio,
  mohamed metwally web developer,
  mohamed metwally front-end,
  mohamed metwally developer,
  mohamed metwally portfolio website,
  portfolio,
  website,
  محمد متولي مطور ويب,
  محمد متولي تطوير الواجهات الأمامية,
  محمد متولي مطور,
  موقع محمد متولي,
  موقع شخصي,
  موقع تعريفي,
  موقع إلكتروني
  front-end developer,
  React.js,
  GSAP,
  Tailwind CSS,
  dynamic web applications,
  responsive web design,
  self-management,
  collaboration,
  high-quality web solutions,
  innovation,
  excellence,
  interactive websites,
  complex web applications,
  extensive experience,
  seamless user experiences,
  creative ideas,
  practical solutions,
  مطور واجهات أمامية,
  React.js,
  GSAP,
  Tailwind CSS,
  تطبيقات الويب الديناميكية,
  تصميم الويب المتجاوب,
  إدارة الذات,
  التعاون,
  حلول الويب عالية الجودة,
  الابتكار,
  التفوق,
  المواقع التفاعلية,
  تطبيقات الويب المعقدة,
  الخبرة الواسعة,
  تجارب المستخدم السلسة,
  الأفكار الإبداعية,
  الحلول العملية,
  Programming,
  Company,
  Creative,
  Technical,
  Services,
  Diverse,
  Expert,
  Consultation,
  Design,
  Installation,
  Maintenance,
  Technological,
  Security,
  Law,
  Legal,
  Consulting,
  Legal services,
  Local,
  International,
  Dealings,
  Vision,
  Value,
  UI,
  Design,
  Hotel,
  Website,
  Addresses,
  Services,
  Reservation,
  Capabilities,
  Creative agency,
  Graphic,
  Design,
  Video,
  Editing,
  User-friendly,
  Platform,
  Intuitive,
  Navigation,
  Visually appealing,
  Layouts,
  Enjoyable,
  Learning,
  E-commerce,
  Website,
  Modern,
  Kitchenware,
  Unique,
  Assortment,
  Graduation,
  Project,
  Programming company,
  Creative technical services,
  Diverse technical services,
  Expert consultation,
  Design and installation,
  Maintenance services,
  Technological needs,
  Security needs,
  Law and legal consulting,
  Legal services,
  Local dealings,
  International dealings,
  Vision and value,
  UI design,
  Hotel website,
  Addresses,
  Reservation capabilities,
  Creative agency,
  Graphic design,
  Video editing services,
  User-friendly platform,
  Intuitive navigation,
  Visually appealing layouts,
  Enjoyable learning,
  E-commerce website,
  Modern kitchenware,
  Unique assortment,
  Graduation project,
  برمجة,
  شركة,
  إبداعي,
  تقني,
  خدمات,
  متنوعة,
  خبير,
  استشارة,
  تصميم,
  تركيب,
  صيانة,
  تكنولوجي,
  أمان,
  قانون,
  قانوني,
  استشارات,
  خدمات قانونية,
  محلي,
  دولي,
  تعاملات,
  رؤية,
  قيمة,
  واجهة المستخدم,
  تصميم,
  فندق,
  موقع إلكتروني,
  عناوين,
  خدمات,
  حجز,
  قدرات,
  وكالة إبداعية,
  تصميم جرافيك,
  فيديو,
  تحرير,
  سهل الاستخدام,
  منصة,
  بديهي,
  تنقل,
  جذاب بصريًا,
  تخطيطات,
  ممتع,
  تعلم,
  تجارة إلكترونية,
  موقع إلكتروني,
  حديث,
  أدوات المطبخ,
  فريد,
  مجموعة,
  تخرج,
  مشروع,
  شركة برمجة,
  خدمات تقنية إبداعية,
  خدمات تقنية متنوعة,
  استشارة خبير,
  تصميم وتركيب,
  خدمات صيانة,
  احتياجات تكنولوجية,
  احتياجات أمان,
  قانون واستشارات قانونية,
  خدمات قانونية,
  تعاملات محلية,
  تعاملات دولية,
  رؤية وقيمة,
  تصميم واجهة المستخدم,
  موقع فندق,
  عناوين,
  قدرات حجز,
  وكالة إبداعية,
  تصميم جرافيك,
  خدمات تحرير الفيديو,
  منصة سهلة الاستخدام,
  تنقل بديهي,
  تخطيطات جذابة بصريًا,
  تعلم ممتع,
  موقع تجارة إلكترونية,
  أدوات مطبخ حديثة,
  مجموعة فريدة,
  مشروع تخرج
" />
          </Helmet>
        </HelmetProvider>
        <div className='enter-container'>
          <div className='enter-abs'>
            <HomeTest />
          </div>
          <div className='chose'>
            <h1>Choose Your Type</h1>
            <div className='chose-btn'>
              <div className='Scolor'>
                <span>
                  <button className='btn Scolors' onClick={() => handleButtonClick(1)}>
                    Go With The Flow <br /> (default)
                  </button>
                </span>
              </div>
              <button className='btn'>
                <a style={{ color: "#242221" }} href="https://mohammed-metwally-classic.web.app/">
                  Classic <br />(recommend for mobile)
                </a>
              </button>
            </div>
            <button className='btn-CV' style={{ margin: "2rem 0 0rem" }}>
              <a className='' href="https://mohammed-metwally-classic.web.app/resume">
                If You Just Want My Resume.
                (Download CV)

              </a>
            </button>
          </div>
        </div>
      </>
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <HelmetProvider>
        <Helmet>

          <title>Mohamed Metwally's Portfolio</title>

        </Helmet>
      </HelmetProvider>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/Gallery" element={<GalleryPage />} />
        </Routes>
      </Router>
    </>
  );
};

export default App;
